import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PortalTooltip } from '@components/PortalTooltip';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { useIsDesktop } from '@utils/helpers';

import { getPictogramIcon } from './constants';
import {
  pictogramMoreInfoPathname,
  pictogramMoreInfoLinkText,
  pictogramTitles,
  pictogramTooltip,
} from './messages';
import { EPictograms } from './model';
import {
  MoreInfoLink,
  PictogramImage,
  PictogramItemWrapper,
  TooltipContent,
} from './styled';
import {
  trackOnClickMoreInfoTooltip,
  trackPictograms,
} from './trackPictograms';

interface IPictogramProps {
  pictogram: EPictograms;
}

export const Pictogram = ({ pictogram }: IPictogramProps) => {
  const { formatMessage } = useIntl();
  const isDesktop = useIsDesktop();

  const moreInfoPathname = formatMessage(pictogramMoreInfoPathname);

  const { getTimeFromInit } = useTrackingContext();

  if (!pictogramTooltip[pictogram] || !pictogramTitles[pictogram]) {
    return null;
  }

  const onShowTooltip = () => {
    trackPictograms(getTimeFromInit(), pictogram, isDesktop);
  };

  const onLinkClick = () => {
    trackOnClickMoreInfoTooltip(getTimeFromInit(), pictogram);
  };

  return (
    <PictogramItemWrapper data-testid="pictogram-item">
      <PortalTooltip
        interactive={true}
        dataTestId="pictogram-tooltip"
        content={
          <PortalTooltip.Content shouldWrapText={false}>
            <TooltipContent>
              <FormattedMessage {...pictogramTooltip[pictogram]} />
              {moreInfoPathname !== 'null' && (
                <MoreInfoLink
                  href={moreInfoPathname}
                  rel="nofollow"
                  target="__blank"
                  onClick={onLinkClick}
                >
                  {formatMessage(pictogramMoreInfoLinkText)}
                </MoreInfoLink>
              )}
            </TooltipContent>
          </PortalTooltip.Content>
        }
        onShow={onShowTooltip}
      >
        <PictogramImage
          src={getPictogramIcon(pictogram)}
          data-testid="pictogram-icon"
          alt={formatMessage(pictogramTooltip[pictogram])}
        />
      </PortalTooltip>
      <FormattedMessage {...pictogramTitles[pictogram]} />
    </PictogramItemWrapper>
  );
};
